import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Box } from '@mui/joy';
import { CssVarsProvider } from '@mui/joy/styles';

import { dualCrewConfig } from '../../config/dualCrewConfig';
import { normalizeDegrees } from '../../general-utilities/numbers-util';
import { serverBaseWebSocketUrl } from '../../general-utilities/urlUtils';
import { useCrewWebSockets } from '../../hooks/useCrewWebSockets';
import { useVoiceChat } from '../../hooks/useVoiceChat';
import { CrewService } from '../../services/CrewService';
import { SharedSessionParticipant, SharedSessionResponse } from '../../services/types/api.generated';
import sessionStore from '../../stores/SessionStore';
import crewTheme from '../../themes/crew';
import CrewToast from '../Crew/CrewToast';
import CrewToastContainer from '../Crew/CrewToastContainer';
import CrewViewHeader from '../Crew/CrewViewHeader';
import { createHomeMarkerImage } from '../Crew/HomeMarker';
import { useCrewMQTT } from '../Crew/hooks/useCrewMQTT';
import { useCrewViewOrientation } from '../Crew/hooks/useCrewViewOrientation';
import LoadingOverlay from '../Crew/LoadingOverlay';
import PortraitPopup from '../Crew/PortraitPopup';
import SettingsAccordion from '../Crew/SettingsAccordion';
import { AgentStatus, TelemetryData } from '../Crew/types';
import { createGimbalIndicatorImage } from '../GimbalIndicator';
import { MAP_CONSTANTS } from '../Mapbox/mapConstants';
import { LiveLineData, MapType, MarkerData } from '../Mapbox/mapTypes';
import SimpleVideoStream from '../SimpleVideoStream';
import DualMapDroneView from './DualMapDroneView';
import DualTelemetryBar from './DualTelemetryBar';
import DualVideoPiP from './DualVideoPiP';

const DRONE_MARKER_SIZE = 30;
const GIMBAL_INDICATOR_SIZE = DRONE_MARKER_SIZE;
// Match the same ratio as normal Crew: GIMBAL_OFFSET_NORMAL(50) / DRONE_MARKER_SIZE(49) ≈ 1.02
const GIMBAL_PIXEL_OFFSET = Math.round(DRONE_MARKER_SIZE * (MAP_CONSTANTS.GIMBAL_OFFSET_NORMAL / 49));
const HOME_POSITION_MARKER_SIZE = 17;
const DRONE_COLORS = ['#FF69B4', '#1E90FF'];

function createColoredDroneMarker(color: string): HTMLImageElement {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <g>
      <polygon fill="${color}" opacity="0.9" points="35.5,2.118 35.5,53.691 1.118,70.882"/>
      <path fill="${color}" opacity="0.6" d="M35,4.236v49.146L2.236,69.764L35,4.236 M36,0L0,72l36-18V0L36,0z"/>
      <polygon fill="${color}" points="36.5,53.691 36.5,2.118 70.882,70.882"/>
      <path fill="${color}" opacity="0.6" d="M37,4.236l32.764,65.528L37,53.382V4.236 M36,0v54l36,18L36,0L36,0z"/>
    </g>
  </svg>`;
  const img = new Image();
  img.src = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  img.width = DRONE_MARKER_SIZE;
  img.height = DRONE_MARKER_SIZE;
  return img;
}

type DualCrewViewProps = {
  participantId: string;
  sessionId: string;
  deviceUuid: string;
  onSessionEnded?: () => void;
};

const DualCrewView: React.FC<DualCrewViewProps> = ({ participantId, sessionId, deviceUuid, onSessionEnded }) => {
  const { t } = useTranslation();
  const { device1Uuid, device2Uuid, callsign1, callsign2, videoUrl1, videoUrl2, mqttUrl, mqttUsername, mqttPassword } =
    dualCrewConfig;

  const [isPending, setIsPending] = useState(false);
  const [mainView, setMainView] = useState<'map' | 'video1' | 'video2'>('map');
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('idle');
  const [followDeviceId, setFollowDeviceId] = useState<string | null>(deviceUuid || null);
  const mapRef = useRef<MapType | null>(null);

  const { showPortraitPopup } = useCrewViewOrientation();

  // Voice chat — single connection shared between components
  const voiceChat = useVoiceChat({
    config: {
      sessionId,
      participantId,
      deviceUuid: device1Uuid
    },
    autoConnect: true
  });

  // Drone marker images — colored to match path lines
  const droneMarker1 = useMemo(() => createColoredDroneMarker(DRONE_COLORS[0]), []);
  const droneMarker2 = useMemo(() => createColoredDroneMarker(DRONE_COLORS[1]), []);

  // Gimbal indicator images — default yellow/gray gradient (same as normal crew)
  const gimbalImage1 = useMemo(() => createGimbalIndicatorImage(GIMBAL_INDICATOR_SIZE), []);
  const gimbalImage2 = useMemo(() => createGimbalIndicatorImage(GIMBAL_INDICATOR_SIZE), []);

  // Home marker images — colored to match each drone
  const homeMarkerImage1 = useMemo(() => createHomeMarkerImage(HOME_POSITION_MARKER_SIZE, DRONE_COLORS[0]), []);
  const homeMarkerImage2 = useMemo(() => createHomeMarkerImage(HOME_POSITION_MARKER_SIZE, DRONE_COLORS[1]), []);

  // LiveLine state for each drone
  const [liveLine1, setLiveLine1] = useState<LiveLineData>({
    lines: [],
    marker: droneMarker1,
    gimbal: gimbalImage1,
    gimbalAngle: 0,
    canUpdateMarkerLocation: true,
    deviceId: device1Uuid
  });

  const [liveLine2, setLiveLine2] = useState<LiveLineData>({
    lines: [],
    marker: droneMarker2,
    gimbal: gimbalImage2,
    gimbalAngle: 0,
    canUpdateMarkerLocation: true,
    deviceId: device2Uuid
  });

  // Per-drone telemetry state (for the telemetry bar)
  const [telemetry1, setTelemetry1] = useState<TelemetryData | null>(null);
  const [telemetry2, setTelemetry2] = useState<TelemetryData | null>(null);

  // Home position markers (colored per drone)
  const [homeMarkers, setHomeMarkers] = useState<{ [id: string]: MarkerData }>({});

  // Session state
  const [participants, setParticipants] = useState<SharedSessionParticipant[]>([]);
  const [sessionData, setSessionData] = useState<SharedSessionResponse | null>(null);

  // Telemetry timestamp refs
  const lastTimestamp1 = useRef<number>(0);
  const lastTimestamp2 = useRef<number>(0);

  // Telemetry handler
  const handleTelemetry = useCallback(
    (data: unknown, setLiveLine: React.Dispatch<React.SetStateAction<LiveLineData>>, lastTimestampRef: React.MutableRefObject<number>) => {
      const telemetryData = data as TelemetryData;
      const timestamp = telemetryData.timestamp?.utc_time_us_rpi
        ? new Date(telemetryData.timestamp.utc_time_us_rpi).getTime()
        : telemetryData.timestamp?.utc_time_us_pixhawk
          ? new Date(telemetryData.timestamp.utc_time_us_pixhawk).getTime()
          : Date.now();

      if (!lastTimestampRef.current || timestamp > lastTimestampRef.current) {
        // eslint-disable-next-line no-param-reassign
        lastTimestampRef.current = timestamp;

        const { position } = telemetryData;
        if (position?.longitude_deg !== undefined && position?.latitude_deg !== undefined) {
          const markerAngle = normalizeDegrees(telemetryData.attitude_euler?.yaw_deg ?? 0);
          const gimbalAngle = normalizeDegrees(markerAngle + normalizeDegrees(telemetryData.gimbal_info?.yaw_deg ?? 0));
          const newPoint: [number, number] = [position.longitude_deg, position.latitude_deg];

          setLiveLine((prev) => {
            const lastPoint = prev.lines[prev.lines.length - 1];
            if (lastPoint && lastPoint[0] === newPoint[0] && lastPoint[1] === newPoint[1]) {
              return { ...prev, markerAngle, gimbalAngle };
            }
            return {
              ...prev,
              lines: [...prev.lines, newPoint],
              markerAngle,
              gimbalAngle
            };
          });
        }
      }
    },
    []
  );

  const handleTelemetry1 = useCallback(
    (data: unknown) => {
      handleTelemetry(data, setLiveLine1, lastTimestamp1);
      setTelemetry1(data as TelemetryData);
      const td = data as TelemetryData;
      if (td.home_position?.latitude_deg != null && td.home_position?.longitude_deg != null) {
        const pos: [number, number] = [td.home_position.longitude_deg, td.home_position.latitude_deg];
        setHomeMarkers((prev) => {
          const existing = prev.home_position_0;
          if (existing && existing.position[0] === pos[0] && existing.position[1] === pos[1]) return prev;
          return { ...prev, home_position_0: { position: pos, image: homeMarkerImage1, id: 'home_position_0' } };
        });
      }
    },
    [handleTelemetry, homeMarkerImage1]
  );

  const handleTelemetry2 = useCallback(
    (data: unknown) => {
      handleTelemetry(data, setLiveLine2, lastTimestamp2);
      setTelemetry2(data as TelemetryData);
      const td = data as TelemetryData;
      if (td.home_position?.latitude_deg != null && td.home_position?.longitude_deg != null) {
        const pos: [number, number] = [td.home_position.longitude_deg, td.home_position.latitude_deg];
        setHomeMarkers((prev) => {
          const existing = prev.home_position_1;
          if (existing && existing.position[0] === pos[0] && existing.position[1] === pos[1]) return prev;
          return { ...prev, home_position_1: { position: pos, image: homeMarkerImage2, id: 'home_position_1' } };
        });
      }
    },
    [handleTelemetry, homeMarkerImage2]
  );

  // MQTT-over-WS connections (2 instances — one per drone)
  const mqtt1 = useCrewMQTT({
    deviceUuid: device1Uuid,
    onTelemetryMessage: handleTelemetry1,
    maxReconnectAttempts: 5,
    mqttUrl,
    mqttUsername,
    mqttPassword
  });

  const mqtt2 = useCrewMQTT({
    deviceUuid: device2Uuid,
    onTelemetryMessage: handleTelemetry2,
    maxReconnectAttempts: 5,
    mqttUrl,
    mqttUsername,
    mqttPassword
  });

  // Ping/pong handler
  const handlePongMessage = useCallback(
    (data: { session: SharedSessionResponse; participants: SharedSessionParticipant[] }) => {
      if (data?.session) {
        setSessionData(data.session);
      }
      if (data?.participants) {
        setParticipants(data.participants);
      }
    },
    []
  );

  // Ping/Pong WebSocket (1 instance — uses deviceUuid from URL)
  const pingUrlBuilder = useCallback(
    (pId: string) =>
      `${serverBaseWebSocketUrl}/ws/shared-session/${sessionId}/participant/${pId}/device/${deviceUuid}/ping`,
    [sessionId, deviceUuid]
  );

  const telemetryUrlBuilder = useCallback(() => '', []);

  const handleEventMessage = useCallback(
    (event: string) => {
      if (event === 'agent_joined') {
        setAgentStatus('active');
      } else if (event === 'agent_left') {
        setAgentStatus('idle');
      }
    },
    []
  );

  const { connect: connectWs, disconnect: disconnectWs } = useCrewWebSockets({
    telemetryUrlBuilder,
    pingUrlBuilder,
    onTelemetryMessage: () => {},
    onPongMessage: handlePongMessage,
    onEventMessage: handleEventMessage,
    onSessionEnded,
    maxReconnectAttempts: 5,
    sessionId,
    deviceUuid
  });

  // Connect on mount
  useEffect(() => {
    const initializeSession = async () => {
      setIsPending(true);
      try {
        mqtt1.connect();
        mqtt2.connect();
        connectWs(participantId);

        const res = await CrewService.getParticipants(sessionId);
        if (res) setParticipants(res);

        const toastMessage = t('crew_view.toast.viewing_session', { sessionName: sessionId });
        toast(CrewToast, { data: { message: toastMessage } });
      } catch (error) {
        console.error('Failed to initialize session:', error);
      } finally {
        setIsPending(false);
      }
    };

    initializeSession();

    return () => {
      mqtt1.disconnect();
      mqtt2.disconnect();
      disconnectWs();
    };
  }, [sessionId, participantId]);

  // Dark theme
  useEffect(() => {
    document.documentElement.setAttribute('data-joy-color-scheme', 'dark');
    return () => {
      document.documentElement.setAttribute(
        'data-joy-color-scheme',
        sessionStore.user?.color_scheme_preference || 'default'
      );
    };
  }, []);

  const onlineParticipants = participants.filter((participant) => participant.is_online);

  const liveLines = useMemo(() => [liveLine1, liveLine2], [liveLine1, liveLine2]);
  const callsigns = useMemo(() => [callsign1, callsign2], [callsign1, callsign2]);
  const telemetries = useMemo(() => [telemetry1, telemetry2], [telemetry1, telemetry2]);

  const mainVideoUrl = mainView === 'video1' ? videoUrl1 : mainView === 'video2' ? videoUrl2 : null;

  return (
    <CssVarsProvider theme={crewTheme} disableTransitionOnChange>
      <Box className='flex flex-col w-full h-screen relative' sx={{ bgcolor: 'background.level1' }}>
        <CrewViewHeader
          sessionName={sessionId}
          link={`${window.location.origin}/crew-dual/${sessionId}/device/${device1Uuid}`}
        />
        <LoadingOverlay isPending={isPending} />

        <Box className='flex-1 relative'>
          {/* Main view — map (always rendered, visibility toggled) */}
          <Box
            className='w-full h-full flex items-center justify-center'
            sx={{
              bgcolor: 'background.level1',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: mainView === 'map' ? 10 : 1,
              opacity: mainView === 'map' ? 1 : 0,
              pointerEvents: mainView === 'map' ? 'auto' : 'none'
            }}
          >
            <DualMapDroneView
              liveLines={liveLines}
              lineColors={DRONE_COLORS}
              callsigns={callsigns}
              followDeviceId={followDeviceId}
              onFollowChange={setFollowDeviceId}
              mapRef={mapRef}
              customZoomControls
              initialZoom={14}
              staticMarkers={homeMarkers}
              gimbalPixelOffset={GIMBAL_PIXEL_OFFSET}
            />
          </Box>

          {/* Main view — full-screen video (when a video is selected) */}
          {mainVideoUrl && (
            <Box
              className='w-full h-full flex items-center justify-center'
              sx={{
                bgcolor: 'background.level1',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10
              }}
            >
              <SimpleVideoStream key={mainVideoUrl} url={mainVideoUrl} width='100%' height='100%' />
            </Box>
          )}

          {/* Settings accordion — bottom-left */}
          <SettingsAccordion
            subTitle={sessionId}
            participants={onlineParticipants}
            sessionId={sessionId}
            participantId={participantId}
            deviceUuid={device1Uuid}
            viewerCount={sessionData?.viewer_count ?? onlineParticipants.length}
            microphoneEnabled={voiceChat.microphoneEnabled}
            isVoiceChatConnected={voiceChat.isConnected}
            isVoiceChatConnecting={voiceChat.isConnecting}
            onToggleMicrophone={async () => {
              await voiceChat.setMicrophoneEnabled(!voiceChat.microphoneEnabled);
            }}
            onToggleVoiceChat={async () => {
              if (voiceChat.isConnected) {
                await voiceChat.disconnect();
              } else {
                await voiceChat.connect();
              }
            }}
            voiceChatParticipants={voiceChat.participants}
            activeSpeakers={voiceChat.activeSpeakers}
            agentStatus={agentStatus}
            setAgentStatus={setAgentStatus}
            droneUuids={[device1Uuid, device2Uuid]}
            onSetMicrophone={async (enabled: boolean) => {
              await voiceChat.setMicrophoneEnabled(enabled);
            }}
          />

          {/* Telemetry bar — bottom-center */}
          <DualTelemetryBar telemetry={telemetries} callsigns={callsigns} colors={DRONE_COLORS} />

          {/* PiPs — shown based on current main view */}
          {mainView === 'map' && (
            <>
              {/* Video 1 PiP — top-right */}
              <DualVideoPiP position='top' borderColor={DRONE_COLORS[0]} onClick={() => setMainView('video1')}>
                <SimpleVideoStream url={videoUrl1} width='100%' height='100%' />
              </DualVideoPiP>

              {/* Video 2 PiP — bottom-right */}
              <DualVideoPiP position='bottom' borderColor={DRONE_COLORS[1]} onClick={() => setMainView('video2')}>
                <SimpleVideoStream url={videoUrl2} width='100%' height='100%' />
              </DualVideoPiP>
            </>
          )}

          {/* Map PiP — bottom-right when a video is the main view */}
          {mainView !== 'map' && (
            <Box
              className='absolute bottom-0 right-0 cursor-pointer'
              style={{ zIndex: 99999 }}
              sx={{
                width: 320,
                height: 180,
                bgcolor: 'background.level1',
                overflow: 'hidden'
              }}
              onClick={() => setMainView('map')}
            >
              <DualMapDroneView
                liveLines={liveLines}
                mapRef={mapRef}
                initialZoom={14}
                hideControls
                inPipMode
                staticMarkers={homeMarkers}
                gimbalPixelOffset={GIMBAL_PIXEL_OFFSET}
              />
            </Box>
          )}
        </Box>
      </Box>

      <CrewToastContainer />
      <PortraitPopup show={showPortraitPopup} />
    </CssVarsProvider>
  );
};

export default DualCrewView;
